import React from 'react';
import { Container } from '@ui';
import { TextField } from '@ui/inputs';
import { SkeletonButton } from '@ui/buttons';
import { Consequence, MicroLabel } from '@features/scoreSpec/styles';
import { isModellable } from './schemaModel';

// The escape hatch, for a shape the row editor will not pretend to understand: an array, an `enum`,
// a third level of nesting.
//
// Cadence copied from the advanced editor's raw pane (RawPane.js:29-40) and it is the important
// part: text is committed on every keystroke that PARSES, and text that does not parse is held here
// and never written. A half-typed brace must not reach the payload, and it must not freeze the rest
// of the page either.
//
// Held text is local state rather than lifted, because this pane only unmounts when the operator
// leaves the contract editor entirely — at which point the edits are discarded anyway.

const print = (value) => JSON.stringify(value === undefined ? {} : value, null, 2);

export const ContractJsonPane = ({ contract }) => {
  const committed = print(contract.rawSchema);
  const [held, setHeld] = React.useState(null);
  const text = held === null ? committed : held;

  let parseError = null;
  if (held !== null) {
    try {
      JSON.parse(held);
    } catch (e) {
      parseError = e.message;
    }
  }

  const onChange = (next) => {
    setHeld(next);
    try {
      contract.patch({ rawSchema: JSON.parse(next) });
    } catch (e) {
      // Held, not written. The previous parsed value stays in the payload.
    }
  };

  const canReturnToRows = !parseError && isModellable(contract.rawSchema);

  return (
    <div>
      <MicroLabel>The answer shape — JSON</MicroLabel>
      <Container top={8}>
        <TextField
          id="duplicate-schema-json"
          aria-label="Answer shape as JSON"
          multiline
          mono
          rows={16}
          value={text}
          onChange={onChange}
          invalid={!!parseError}
          hint={parseError ? `Not valid JSON — ${parseError}. Nothing is written until it parses.` : undefined}
        />
      </Container>
      <Container top={8}>
        <Consequence>
          {contract.isModellable
            ? 'Every key strict mode allows is editable here. The field editor covers the common shapes.'
            : 'This shape uses something the field editor does not model — an array, an enum, or a third level of nesting — so it is edited here rather than flattened into rows.'}
        </Consequence>
      </Container>
      {canReturnToRows && (
        <Container top={16}>
          <SkeletonButton
            onClick={() => {
              setHeld(null);
              contract.exitRaw();
            }}
          >
            Back to the field editor
          </SkeletonButton>
        </Container>
      )}
    </div>
  );
};
